import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { alpha, Box, ClickAwayListener, Popper } from '@mui/material';
import MuiInputAdornment from '@mui/material/InputAdornment';
import useAutocomplete from '@mui/material/useAutocomplete';
import * as React from 'react';
import { Button } from '../button';
import { IconMoreHoriz, IconSearch } from '../icon';
import { Paper } from '../paper';
import { styled } from '../styled';
import { TextField } from '../textfield';
import { withVersion } from '../utils';
const CONTAINER_OFFSET = 16;
const maxSizeModifier = {
    name: 'maxSize',
    enabled: true,
    phase: 'beforeWrite',
    fn({ state }) {
        const { placement, rects, elements } = state;
        const isTop = placement.startsWith('top');
        const available = isTop
            ? rects.reference.y
            : window.innerHeight - rects.reference.y - rects.reference.height;
        const offsetValue = state.modifiersData.offset?.[placement]?.y ?? 0;
        elements.popper.style.maxHeight = `${available - Math.abs(offsetValue) - CONTAINER_OFFSET}px`;
    }
};
const DropdownContainer = styled(Paper)({
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'inherit',
    overflow: 'hidden'
});
const DropdownStickySearchContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper
}));
const DropdownListbox = styled('ul')({
    overflow: 'auto',
    minHeight: 0,
    margin: 0,
    padding: 0,
    listStyle: 'none',
    outline: 'none'
});
const DropdownOptionItem = styled('li')(({ theme }) => ({
    padding: [theme.spacing(1.5), theme.spacing(1.125)],
    typography: theme.typography.body2,
    cursor: 'pointer',
    listStyle: 'none',
    outline: 'none',
    '&:hover, &.Mui-focused, &[aria-selected="true"]': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15)
    }
}));
const StyledButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.action.focus,
    border: 'none'
}));
const defaultSearch = (options, inputValue) => {
    const loweredInputValue = inputValue?.toLowerCase().trim();
    return !loweredInputValue
        ? options
        : options.filter(({ label }) => label?.toLowerCase().includes(loweredInputValue));
};
const DropdownContent = React.forwardRef(function DropdownContent(props, ref) {
    const { options, selected, onSelect, onSearch, onClose } = props;
    const [inputValue, setInputValue] = React.useState(selected?.label ?? '');
    const { getInputProps, getListboxProps, getOptionProps, groupedOptions, getRootProps } = useAutocomplete({
        open: true,
        value: selected,
        onClose: (_, reason) => {
            if (reason === 'escape' || reason === 'selectOption') {
                onClose();
            }
        },
        inputValue,
        onInputChange: (_, value, reason) => {
            if (reason === 'reset') {
                setInputValue('');
                return;
            }
            setInputValue(value);
        },
        options,
        disableClearable: true,
        getOptionLabel: (option) => option.label ?? '',
        isOptionEqualToValue: (a, b) => a.value === b.value,
        filterOptions: (opts, state) => onSearch(opts, state.inputValue),
        onChange: (_e, value) => {
            onSelect(value ?? undefined);
            onClose();
        }
    });
    return (_jsxs(DropdownContainer, { elevation: "L", ref: ref, ...getRootProps(), children: [_jsx(DropdownStickySearchContainer, { children: _jsx(TextField, { autoFocus: true, clearable: true, size: "small", value: inputValue, onChange: setInputValue, slotProps: {
                        input: {
                            startAdornment: (_jsx(MuiInputAdornment, { position: "start", children: _jsx(IconSearch, { sx: { color: 'action.active' } }) }))
                        },
                        htmlInput: { ...getInputProps() }
                    } }) }), groupedOptions.length > 0 && (_jsx(DropdownListbox, { component: "ul", ...getListboxProps(), children: groupedOptions.map((option, index) => {
                    const { key, ...optionElProps } = getOptionProps({
                        option,
                        index
                    });
                    return (_jsx(DropdownOptionItem, { component: "li", ...optionElProps, children: option.label }, key));
                }) }))] }));
});
function AdvancedFilterPropertyImpl(props) {
    const { selected, onSearch = defaultSearch } = props;
    const anchorRef = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const handleToggle = (e) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
    };
    const handleClose = () => setOpen(false);
    return (_jsxs(_Fragment, { children: [_jsx(StyledButton, { onClick: handleToggle, ref: anchorRef, onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle(e);
                    }
                }, variant: "outlined", size: selected ? 'medium' : 'small', children: selected?.label ?? (_jsx(IconMoreHoriz, { color: "primary.main", filled: open })) }), _jsx(Popper, { open: open, anchorEl: anchorRef.current, placement: "bottom-start", modifiers: [maxSizeModifier], sx: (theme) => ({
                    zIndex: theme.zIndex.tooltip
                }), children: _jsx(ClickAwayListener, { onClickAway: handleClose, children: _jsx(DropdownContent, { ...props, onSearch: onSearch, onClose: handleClose }) }) })] }));
}
export const AdvancedFilterProperty = withVersion(AdvancedFilterPropertyImpl, '1.0.0');
